import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { API_URL, authFetch, parseJsonResponse } from "../utils/auth";
import ToolSidebar from "../utils/ToolSideBar";
import "../../css/client/ClientConversationList.css";

function participantName(conversation) {
  return conversation.other_participant_short_name || conversation.other_participant_name || conversation.other_participant_full_name || "Conversation";
}

function participantRole(conversation) {
  return conversation.other_participant_role_label || conversation.other_participant_role || "Portal contact";
}

function initialsFor(conversation) {
  const label = participantName(conversation) || conversation.subject || "Conversation";
  return label.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join("").toUpperCase() || "M";
}

export default function MessagesPage({ partner }) {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [readFilter, setReadFilter] = useState("");

  useEffect(() => {
    async function loadConversations() {
      setLoading(true);
      const response = await authFetch(API_URL + "/conversations/");
      const data = await parseJsonResponse(response);
      if (response.ok) setConversations(Array.isArray(data?.results) ? data.results : []);
      else setConversations([]);
      setLoading(false);
    }
    loadConversations();
  }, []);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return conversations.filter((conversation) => {
      const matchesSearch = !needle ||
        (conversation.subject || "").toLowerCase().includes(needle) ||
        participantName(conversation).toLowerCase().includes(needle) ||
        participantRole(conversation).toLowerCase().includes(needle) ||
        (conversation.latest_message_preview || "").toLowerCase().includes(needle);
      const matchesRead = !readFilter || (readFilter === "unread" ? conversation.unread_count > 0 : conversation.unread_count === 0);
      return matchesSearch && matchesRead;
    });
  }, [conversations, search, readFilter]);

  return (
    <div className="staff-shell">
      <ToolSidebar active="inbox" isPartner={partner} />
      <main className="staff-page conversation-workspace">
        <header className="conversation-header-bar">
          <div>
            <span>Inbox</span>
            <h1>Messages</h1>
            <p>Conversations are limited to approved portal contacts.</p>
          </div>
          <Link className="new-conversation-button" to="/contacts">New Conversation</Link>
        </header>

        <section className="staff-card conversation-toolbar">
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search messages" />
          <select value={readFilter} onChange={(e) => setReadFilter(e.target.value)}>
            <option value="">All messages</option>
            <option value="unread">Unread</option>
            <option value="read">Read</option>
          </select>
        </section>

        {loading ? (
          <section className="staff-card"><p className="staff-empty">Loading conversations...</p></section>
        ) : filtered.length === 0 ? (
          <section className="empty-state"><h3>No conversations yet</h3><p>Start a conversation from Contacts when you need to message someone.</p></section>
        ) : (
          <section className="conversation-list">
            {filtered.map((conversation) => (
              <Link key={conversation.id} to={"/messages/" + conversation.id} className="conversation-card identity-card">
                <div className="conversation-card-top">
                  <div className="identity-main">
                    <div className="identity-avatar">{initialsFor(conversation)}</div>
                    <div className="identity-copy">
                      <div className="identity-heading">
                        <h3>{participantName(conversation)}</h3>
                        <span className="role-badge">{participantRole(conversation)}</span>
                      </div>
                      {conversation.subject && <strong className="conversation-subject compact">{conversation.subject}</strong>}
                    </div>
                  </div>
                  {conversation.unread_count > 0 && <strong className="unread-badge">{conversation.unread_count}</strong>}
                </div>
                <p className="conversation-preview">{conversation.latest_message_preview || "No messages yet"}</p>
                <small>{conversation.latest_message_at ? new Date(conversation.latest_message_at).toLocaleString() : ""}</small>
              </Link>
            ))}
          </section>
        )}
      </main>
    </div>
  );
}